import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CityCardLink from "./CityCardLink";
import { getDeptNameByCode } from "../data/deptNamesByCode";
import ThreadJsonLd from "./ThreadJsonLd";
import ProsMarquee from "../pages/blog/ProsMarquee";
import { roundRobinPartition } from "../utils/roundRobinPartition";

/**
 * Mini-thread SEO : Voyance & vignobles / terroirs
 * Encart visuel + maillage interne des villes viticoles et de terroir.
 * Chaque ville possède un `terroir` (badge court) + une `justification` (contexte détaillé
 * affiché sous la liste, pour SEO et transparence éditoriale).
 */
const WINE_CITIES = [
  // Champagne
  { slug: "reims", name: "Reims", code: "51", terroir: "Champagne",
    justification: "Capitale de la Champagne, sacre des rois et grandes maisons de champagne." },
  { slug: "troyes", name: "Troyes", code: "10", terroir: "Champagne",
    justification: "Côte des Bar, portes sud du vignoble champenois." },
  // Alsace
  { slug: "colmar", name: "Colmar", code: "68", terroir: "Alsace",
    justification: "Capitale des vins d'Alsace, cœur de la route des vins et des grands crus alsaciens." },
  { slug: "mulhouse", name: "Mulhouse", code: "68", terroir: "Alsace",
    justification: "Sud-Alsace, à proximité immédiate de la route des vins et du vignoble AOC Alsace." },
  { slug: "strasbourg", name: "Strasbourg", code: "67", terroir: "Alsace",
    justification: "Porte nord de la route des vins d'Alsace, capitale historique du vignoble AOC." },
  // Loire
  { slug: "angers", name: "Angers", code: "49", terroir: "Loire",
    justification: "Cœur de l'Anjou viticole (AOC Anjou, Coteaux-du-Layon, Savennières)." },
  { slug: "saumur", name: "Saumur", code: "49", terroir: "Loire",
    justification: "AOC Saumur-Champigny, Crémant de Loire — grande cité viticole de la Loire." },
  { slug: "tours", name: "Tours", code: "37", terroir: "Loire",
    justification: "Val de Loire viticole (Vouvray, Chinon, Bourgueil, Montlouis)." },
  // Bourgogne / Jura
  { slug: "auxerre", name: "Auxerre", code: "89", terroir: "Bourgogne-Jura",
    justification: "Basse Bourgogne, proche du vignoble de Chablis et de l'Yonne AOC." },
  { slug: "dijon", name: "Dijon", code: "21", terroir: "Bourgogne-Jura",
    justification: "Porte nord de la Côte de Nuits, capitale historique des ducs de Bourgogne viticole." },
  { slug: "macon", name: "Mâcon", code: "71", terroir: "Bourgogne-Jura",
    justification: "Capitale du Mâconnais (Pouilly-Fuissé, Saint-Véran)." },
  { slug: "chalon-sur-saone", name: "Chalon-sur-Saône", code: "71", terroir: "Bourgogne-Jura",
    justification: "Cœur de la Côte chalonnaise (Mercurey, Rully, Givry)." },
  { slug: "besancon", name: "Besançon", code: "25", terroir: "Bourgogne-Jura",
    justification: "Capitale de Franche-Comté, proche du vignoble AOC du Jura (Arbois, Château-Chalon)." },
  // Côtes du Rhône
  { slug: "avignon", name: "Avignon", code: "84", terroir: "Rhône",
    justification: "Cité papale au cœur des Côtes-du-Rhône (Châteauneuf-du-Pape, Tavel, Gigondas)." },
  { slug: "valence", name: "Valence", code: "26", terroir: "Rhône",
    justification: "Porte des Côtes-du-Rhône septentrionales (Crozes-Hermitage, Saint-Joseph)." },
  { slug: "nimes", name: "Nîmes", code: "30", terroir: "Rhône",
    justification: "Costières de Nîmes, terroir méridional entre Rhône et Camargue." },
  // Languedoc-Roussillon
  { slug: "narbonne", name: "Narbonne", code: "11", terroir: "Languedoc",
    justification: "Corbières, Fitou, Minervois — capitale historique du Languedoc viticole." },
  { slug: "carcassonne", name: "Carcassonne", code: "11", terroir: "Languedoc",
    justification: "Cité médiévale au cœur du Cabardès, du Minervois et de la Malepère." },
  { slug: "sete", name: "Sète", code: "34", terroir: "Languedoc",
    justification: "Muscat de Frontignan, Picpoul de Pinet, terroir de bord d'étang." },
  { slug: "beziers", name: "Béziers", code: "34", terroir: "Languedoc",
    justification: "Faugères, Saint-Chinian — capitale historique du vignoble biterrois." },
  { slug: "perpignan", name: "Perpignan", code: "66", terroir: "Roussillon",
    justification: "Côtes-du-Roussillon, Maury, Rivesaltes — cœur des vins doux naturels." },
  // Sud-Ouest
  { slug: "bergerac", name: "Bergerac", code: "24", terroir: "Sud-Ouest",
    justification: "AOC Bergerac, Monbazillac, Pécharmant — grand vignoble du Périgord." },
  { slug: "albi", name: "Albi", code: "81", terroir: "Sud-Ouest",
    justification: "Gaillac, l'un des plus anciens vignobles de France (AOC millénaire)." },
  { slug: "montauban", name: "Montauban", code: "82", terroir: "Sud-Ouest",
    justification: "Proche des vignobles de Fronton et du Quercy AOC." },
  { slug: "pau", name: "Pau", code: "64", terroir: "Sud-Ouest",
    justification: "Vignoble AOC Jurançon (moelleux et secs de Béarn), cœur du Piémont pyrénéen viticole." },
  { slug: "tarbes", name: "Tarbes", code: "65", terroir: "Sud-Ouest",
    justification: "Bigorre viticole, à la porte du Madiran et du Pacherenc-du-Vic-Bilh AOC." },
  { slug: "pessac", name: "Pessac", code: "33", terroir: "Sud-Ouest",
    justification: "Pessac-Léognan, appellation phare des Graves bordelais." },
  { slug: "bordeaux", name: "Bordeaux", code: "33", terroir: "Sud-Ouest",
    justification: "Capitale mondiale du vin, cœur du Bordelais et de ses 65 AOC." },
  // Bresse-Beaujolais
  { slug: "bourg-en-bresse", name: "Bourg-en-Bresse", code: "01", terroir: "Bresse-Beaujolais",
    justification: "Bresse gastronomique (volaille de Bresse AOC), à la porte du Beaujolais et des Coteaux-du-Bugey." },
];

const TERROIR_COLORS = {
  Champagne: "from-yellow-300/20 to-amber-500/10 border-yellow-300/40 text-yellow-100",
  Alsace: "from-lime-300/20 to-emerald-500/10 border-lime-300/40 text-lime-100",
  Loire: "from-cyan-300/20 to-teal-500/10 border-cyan-300/40 text-cyan-100",
  "Bourgogne-Jura": "from-rose-300/20 to-red-700/10 border-rose-300/40 text-rose-100",
  Rhône: "from-orange-300/20 to-red-600/10 border-orange-300/40 text-orange-100",
  Languedoc: "from-purple-300/20 to-fuchsia-700/10 border-purple-300/40 text-purple-100",
  Roussillon: "from-pink-300/20 to-rose-700/10 border-pink-300/40 text-pink-100",
  "Sud-Ouest": "from-red-300/20 to-rose-800/10 border-red-300/40 text-red-100",
  "Bresse-Beaujolais": "from-amber-300/20 to-yellow-600/10 border-amber-300/40 text-amber-100",
};

const MiniThreadVignoblesTerroir = ({ currentSlug = "" }) => {
  const cities = WINE_CITIES.filter((c) => c.slug !== currentSlug);

  // Pool AD FRANCE pour round-robin sur 2 marquees AD
  const [poolAD, setPoolAD] = useState([]);
  useEffect(() => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const isFrance = (p) => p.nbDepartements === 999 || p.nbDepartements >= 4;
    let cancelled = false;
    fetch(`${API}/voyants/marquee?cat=div&limit=300`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setPoolAD(Array.isArray(data) ? data.filter(isFrance) : []);
      })
      .catch(() => { if (!cancelled) setPoolAD([]); });
    return () => { cancelled = true; };
  }, []);
  const groupsAD = roundRobinPartition(poolAD, 4);
  const emptyLabelAD = "💛 Bientôt — professionnels en cours d’accréditation";
  const emptyLabelPE = "🌱 Bientôt — professionnels énergétiques en cours d’accréditation";

  // Découpage : Marquee 1 sous rangée 1 (Reims, Troyes, Colmar, Mulhouse)
  //             Marquee 2 sous rangée 3 (…, Auxerre en fin de rangée 3)
  //             Marquee 3 sous rangée 5
  //             Marquee 4 sous rangée 7
  // Avec 4 cards/rangée sur md+ :
  //  Bloc A = cities[0..3]   (rangée 1)
  //  Bloc B = cities[4..11]  (rangées 2-3, se termine par Auxerre/Dijon/Mâcon/Chalon)
  //  Bloc C = cities[12..19] (rangées 4-5)
  //  Bloc D = cities[20..27] (rangées 6-7)
  //  Bloc E = cities[28..]   (rangée 8 restante)
  const blockA = cities.slice(0, 4);
  const blockB = cities.slice(4, 12);
  const blockC = cities.slice(12, 20);
  const blockD = cities.slice(20, 28);
  const blockE = cities.slice(28);

  const renderCityBlock = (block) => (
    <ul
      aria-label="Villes viticoles"
      className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4"
    >
      {block.map((c) => {
        const colorCls = TERROIR_COLORS[c.terroir] || TERROIR_COLORS.Rhône;
        return (
          <li key={c.slug}>
            <CityCardLink
              villeName={c.name}
              deptCode={c.code}
              deptName={getDeptNameByCode(c.code)}
              source="mini-thread-vignobles-terroirs"
              kind="voyance"
              to={`/voyance-${c.slug}-${c.code.toLowerCase()}`}
              data-testid={`mini-thread-vignobles-link-${c.slug}`}
              className={`group block px-4 py-3 rounded-xl bg-gradient-to-br ${colorCls} border backdrop-blur-sm hover:scale-105 hover:shadow-lg hover:shadow-amber-500/20 transition-all duration-300`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-semibold text-white text-lg md:text-base">
                  {c.name}
                </span>
                <span className="text-sm md:text-[10px] opacity-70 font-mono">
                  {c.code}
                </span>
              </div>
              <div className="text-[14px] md:text-[11px] uppercase tracking-wider mt-1 opacity-80">
                {c.terroir}
              </div>
              <div className="mt-2 text-[14px] md:text-[12.5px] leading-relaxed text-white/95 md:text-white/80 italic normal-case tracking-normal">
                {c.justification}
              </div>
            </CityCardLink>
          </li>
        );
      })}
    </ul>
  );

  const marqueeWrap = { maxWidth: '900px', margin: '2rem auto', padding: '0 1rem' };

  return (
    <section
      className="py-16 relative overflow-hidden"
      data-testid="mini-thread-vignobles"
      aria-labelledby="mini-thread-vignobles-title"
    >
      <ThreadJsonLd
        name="Voyance & vignobles — terroirs de France"
        description="Cités viticoles françaises (Champagne, Alsace, Loire, Rhône, Bourgogne, Bordeaux, Languedoc, Sud-Ouest) où la voyance se déguste comme un grand cru."
        hubUrl="https://jaimelavoyance.fr/voyance-thematique-france/vignobles-terroirs"
        cities={WINE_CITIES}
      />
      {/* Fond dégradé bordeaux / pourpre / or */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, #1a0812 0%, #3d0f24 45%, #1a0812 100%)",
        }}
      />
      {/* Halo lumineux (or antique + pourpre) */}
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            "radial-gradient(ellipse at 25% 30%, rgba(217,119,6,0.18) 0%, transparent 55%), radial-gradient(ellipse at 75% 70%, rgba(190,24,93,0.18) 0%, transparent 55%)",
        }}
      />
      {/* SVG feuilles de vigne discrètes */}
      <svg
        className="absolute top-6 right-6 w-20 h-20 opacity-20"
        viewBox="0 0 100 100"
        aria-hidden="true"
      >
        <path
          d="M50 15 C40 25, 25 25, 20 40 C25 45, 30 42, 32 50 C22 55, 22 65, 28 72 C35 68, 42 68, 45 78 C48 78, 50 72, 50 65 C55 72, 62 72, 68 65 C65 60, 60 58, 62 52 C70 50, 72 42, 68 35 C60 40, 55 38, 52 30 C58 25, 58 18, 50 15 Z"
          fill="rgba(217,119,6,0.35)"
          stroke="rgba(217,119,6,0.6)"
          strokeWidth="0.8"
        />
      </svg>
      <svg
        className="absolute bottom-6 left-6 w-16 h-16 opacity-15"
        viewBox="0 0 100 100"
        aria-hidden="true"
      >
        <path
          d="M50 15 C40 25, 25 25, 20 40 C25 45, 30 42, 32 50 C22 55, 22 65, 28 72 C35 68, 42 68, 45 78 C48 78, 50 72, 50 65 C55 72, 62 72, 68 65 C65 60, 60 58, 62 52 C70 50, 72 42, 68 35 C60 40, 55 38, 52 30 C58 25, 58 18, 50 15 Z"
          fill="rgba(190,24,93,0.35)"
          stroke="rgba(190,24,93,0.6)"
          strokeWidth="0.8"
        />
      </svg>

      <div className="relative z-10 max-w-6xl mx-auto px-4">
        {/* H2/description/tagline supprimés — unifiés dans le HERO
            de MiniThreadDetailPage (voir threadsConfig.js pour la description longue). */}

        {/* Rangée 1 : Reims, Troyes, Colmar, Mulhouse */}
        <div id="mini-thread-vignobles-title">{renderCityBlock(blockA)}</div>

        {/* ==== Marquee AD #1 (avec Brice) — sous rangée 1 ==== */}
        <div style={marqueeWrap} data-testid="vignobles-marquee-ad-1">
          <ProsMarquee
            cat="div"
            franceOnly
            titleSuffixTricolore
            keepEmpty
            emptyStateLabel={emptyLabelAD}
            prefetchedPros={groupsAD[0]}
          />
        </div>

        {/* Rangées 2-3 : Strasbourg, Angers, Saumur, Tours, Auxerre, Dijon, Mâcon, Chalon */}
        {renderCityBlock(blockB)}

        {/* ==== Marquee PE #1 (empty) — sous rangée 3 (Auxerre) ==== */}
        <div style={marqueeWrap} data-testid="vignobles-marquee-ad-2">
          <ProsMarquee
            cat="div"
            franceOnly
            titleSuffixTricolore
            keepEmpty
            emptyStateLabel={emptyLabelAD} prefetchedPros={groupsAD[1]} />
        </div>

        {/* Rangées 4-5 : Besançon, Avignon, Valence, Nîmes, Narbonne, Carcassonne, Sète, Béziers */}
        {renderCityBlock(blockC)}

        {/* ==== Marquee AD #2 (empty) — sous rangée 5 ==== */}
        <div style={marqueeWrap} data-testid="vignobles-marquee-ad-3">
          <ProsMarquee
            cat="div"
            franceOnly
            titleSuffixTricolore
            keepEmpty
            emptyStateLabel={emptyLabelAD}
            prefetchedPros={groupsAD[2]}
          />
        </div>

        {/* Rangées 6-7 : Perpignan, Bergerac, Albi, Montauban, Pau, Tarbes, Pessac, Bordeaux */}
        {renderCityBlock(blockD)}

        {/* ==== Marquee PE #2 (empty) — sous rangée 7 ==== */}
        <div style={marqueeWrap} data-testid="vignobles-marquee-ad-4">
          <ProsMarquee
            cat="div"
            franceOnly
            titleSuffixTricolore
            keepEmpty
            emptyStateLabel={emptyLabelAD} prefetchedPros={groupsAD[3]} />
        </div>

        {/* Rangée 8 (reste) : Bourg-en-Bresse */}
        {blockE.length > 0 && renderCityBlock(blockE)}

        {/* ============ Section « Pourquoi ces villes ? » — justifications éditoriales ============ */}
        <div
          className="hidden mt-12 pt-8 border-t border-amber-300/20"
          data-testid="mini-thread-vignobles-justifications"
        >
          <h3 className="text-center text-amber-200 text-sm font-semibold tracking-[0.25em] uppercase mb-6">
            Pourquoi ces villes dans le thread Vignobles &amp; Terroirs ?
          </h3>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 max-w-5xl mx-auto text-base">
            {cities.map((c) => (
              <div
                key={`just-${c.slug}`}
                className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 leading-relaxed"
              >
                <dt className="font-semibold text-amber-100 whitespace-nowrap">
                  {c.name} <span className="text-amber-200/50 text-xs font-mono">({c.code})</span> :
                </dt>
                <dd className="text-white italic">{c.justification}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
};

export default MiniThreadVignoblesTerroir;
